import React from "react";

import {
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DownloadIcon from "mdi-react/DownloadIcon";
import FileIcon from "mdi-react/FileIcon";
import FileImageIcon from "mdi-react/FileImageIcon";
import FilePdfBoxIcon from "mdi-react/FilePdfBoxIcon";
import { Controller, useWatch } from "react-hook-form";

import { msgs } from "@config/conf";

import {
  getPluralSuffix,
  validateMaxLength,
  isValidNormalText,
} from "../helpers";

const ConditionalFieldRenderer = ({ fields = [], control, errors }) => {
  const useStyles = makeStyles((theme) => ({
    container: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "8px",
    },
    uploadLabel: {
      display: "inline-block",
      padding: "8px 16px",
      backgroundColor: "#1976d2",
      color: "#fff",
      borderRadius: "4px",
      cursor: "pointer",
      marginBottom: "8px",
    },
    fileDisplay: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "8px",
    },
    fileInfo: {
      display: "flex",
      alignItems: "center",
    },
    fileIcon: {
      fontSize: "24px",
      marginRight: "8px",
    },
    downloadButton: {
      marginRight: "8px",
      padding: "1px",
      backgroundColor: "#4caf50",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    deleteButton: {
      padding: "5px 6px",
      backgroundColor: "#f44336",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
    },
    formControl: {
      padding: "4px 0",
      marginBottom: "8px",
      border: "none",
    },
    labelText: {
      fontSize: "14px",
      marginBottom: "4px",
      color: "inherit",
    },
    labelError: {
      color: "red",
    },
    helperText: {
      marginTop: "2px",
      fontSize: "11px",
      lineHeight: "1.2",
      color: "red",
    },
  }));
  const classes = useStyles();

  const watchedValues = useWatch({ control });
  return (
    <Grid container spacing={2}>
      <>
        {fields.map((field) => {
          const error = errors[field.name];
          const renderConditionalField = () => {
            switch (field.type) {
              case "text":
              case "number":
              case "date":
                return (
                  <Grid item xs={6} key={field.name}>
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={{
                        required: field.required ? msgs.fieldError : false,
                        validate: (value) => {
                          if (validateMaxLength(value, field.maxLength)) {
                            return `Maximum length of ${field.maxLength} character${getPluralSuffix(field.maxLength)} exceeded`;
                          }
                          if (
                            field.validator === "normalText" &&
                            !isValidNormalText(value)
                          ) {
                            return msgs.normalTextError;
                          }
                          return true;
                        },
                        pattern:
                          field.validator === "ssn"
                            ? {
                                value:
                                  /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
                                message:
                                  "Invalid SSN format (e.g., 123-45-6789) and must not start with 000, 666, or 900–999",
                              }
                            : field.validator === "accountNumber"
                              ? {
                                  value: /^\d{5,17}$/,
                                  message:
                                    "Account number must be numeric and between 5 to 17 digits",
                                }
                              : field.validator === "routingNumber"
                                ? {
                                    value: /^\d{9}$/,
                                    message:
                                      "Routing number must be exactly 9 digits",
                                  }
                                : field.validator === "zip"
                                  ? {
                                      value: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
                                      message:
                                        "Zip Code must be formatted as #####-####",
                                    }
                                  : undefined,
                      }}
                      render={({ field: controllerField }) => (
                        <TextField
                          {...controllerField}
                          label={field.label}
                          type={field.type}
                          fullWidth
                          inputProps={{
                            maxLength:
                              field.validator === "accountNumber"
                                ? 17
                                : field.validator === "routingNumber"
                                  ? 9
                                  : field.validator === "zip"
                                    ? 10
                                    : undefined,
                            ...(field.validator === "accountNumber" ||
                            field.validator === "routingNumber"
                              ? {
                                  inputMode: "numeric",
                                  pattern: "\\d*",
                                }
                              : field.validator === "zip" ||
                                  field.validator === "ssn"
                                ? {
                                    inputMode: "numeric",
                                    // No pattern here to avoid browser validation conflict
                                  }
                                : {}),
                          }}
                          margin="normal"
                          error={!!error}
                          helperText={error?.message}
                          InputLabelProps={
                            field.type === "date" ? { shrink: true } : {}
                          }
                          onChange={(e) => {
                            let value = e.target.value;

                            if (field.validator === "zip") {
                              const digits = e.target.value.replace(
                                /[^\d]/g,
                                ""
                              );
                              const formatted =
                                digits.length > 5
                                  ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                                  : digits;
                              controllerField.onChange(formatted);
                            } else if (field.validator === "ssn") {
                              value = value.replace(/\D/g, "");
                              let formattedValue = value;
                              if (value.length > 3 && value.length <= 5) {
                                formattedValue = `${value.slice(0, 3)}-${value.slice(3)}`;
                              } else if (value.length > 5) {
                                formattedValue = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5, 9)}`;
                              }
                              controllerField.onChange(formattedValue);
                            } else if (
                              field.validator === "accountNumber" ||
                              field.validator === "routingNumber"
                            ) {
                              controllerField.onChange(
                                value.replace(/\D/g, "")
                              );
                            } else {
                              controllerField.onChange(value);
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                );

              case "select":
                return (
                  <Grid item xs={6} key={field.name}>
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={{
                        required: field.required ? msgs.fieldError : false,
                      }}
                      render={({ field: controllerField }) => (
                        <TextField
                          {...controllerField}
                          select
                          label={field.label}
                          fullWidth
                          margin="normal"
                          error={!!error}
                          helperText={error?.message}
                        >
                          {field.options.map((opt) =>
                            typeof opt === "string" ? (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ) : (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      )}
                    />
                  </Grid>
                );
              case "file":
                return (
                  <Grid item xs={6} key={field.name}>
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (field.required) {
                            if (!value || !value.base64) {
                              return msgs.fieldError;
                            }

                            const mimeMatch = /^data:(.*);base64,/.exec(
                              value.base64
                            );
                            const mimeType =
                              mimeMatch?.[1] || value.contentType;

                            const allowedTypes = [
                              "application/pdf",
                              "image/jpeg",
                              "image/jpg",
                              "image/png",
                              "image/gif",
                              "image/bmp",
                            ];

                            if (!allowedTypes.includes(mimeType)) {
                              return "Only PDF, JPEG, JPG, PNG, GIF, BMP files are allowed.";
                            }

                            return true;
                          }

                          return true;
                        },
                      }}
                      render={({ field: controllerField }) => {
                        const value = controllerField.value;
                        const hasFile = value?.base64;

                        const getFileIcon = (mimeType) => {
                          if (mimeType.includes("pdf"))
                            return <FilePdfBoxIcon size={24} />;
                          if (mimeType.includes("image"))
                            return <FileImageIcon size={24} />;
                          return <FileIcon size={24} />;
                        };

                        return (
                          <div
                            className="file-upload"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const file = e.dataTransfer.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  const base64 = reader.result;
                                  const mimeMatch = /^data:(.*);base64,/.exec(
                                    base64
                                  );
                                  const mimeType = mimeMatch?.[1] || "";

                                  controllerField.onChange({
                                    base64,
                                    fileName: file.name,
                                    contentType: mimeType,
                                  });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{
                              border: errors?.[field.name]
                                ? "1px solid red"
                                : "2px dashed #ccc",
                              padding: "16px",
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              marginBottom: "12px",
                            }}
                          >
                            <Typography
                              style={{
                                color: errors?.[field.name] ? "red" : "inherit",
                                marginBottom: "8px",
                              }}
                            >
                              {field.label}
                            </Typography>

                            {!hasFile ? (
                              <div className={classes.container}>
                                <label className={classes.uploadLabel}>
                                  Upload
                                  <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.jpeg,.jpg,.png,.gif,.bmp"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          const base64 = reader.result;
                                          const mimeMatch =
                                            /^data:(.*);base64,/.exec(base64);
                                          const mimeType = mimeMatch?.[1] || "";

                                          controllerField.onChange({
                                            base64,
                                            fileName: file.name,
                                            contentType: mimeType,
                                          });
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                </label>
                                <Typography variant="body2">
                                  Or drag and drop a file here
                                </Typography>
                              </div>
                            ) : (
                              <div className={classes.fileDisplay}>
                                <div className={classes.fileInfo}>
                                  <span className={classes.fileIcon}>
                                    {getFileIcon(value.contentType)}
                                  </span>
                                  <Typography>{value.fileName}</Typography>
                                </div>
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const link = document.createElement("a");
                                      link.href = value.base64;
                                      link.download = value.fileName;
                                      link.click();
                                    }}
                                    className={classes.downloadButton}
                                  >
                                    <DownloadIcon />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      controllerField.onChange({
                                        base64: "",
                                        fileName: "",
                                        contentType: "",
                                      })
                                    }
                                    className={classes.deleteButton}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            )}

                            {errors?.[field.name] && (
                              <span style={{ color: "red", fontSize: "12px" }}>
                                {errors[field.name].message}
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </Grid>
                );
              case "radio":
                return (
                  <Grid item xs={6} key={field.name}>
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={{
                        required: field.required ? msgs.fieldError : false,
                      }}
                      render={({ field: controllerField }) => (
                        <FormControl
                          component="fieldset"
                          error={!!errors?.[field.name]}
                          className={classes.formControl}
                        >
                          <Typography
                            className={
                              errors?.[field.name]
                                ? `${classes.labelText} ${classes.labelError}`
                                : classes.labelText
                            }
                          >
                            {field.label}
                          </Typography>
                          <RadioGroup
                            {...controllerField}
                            value={controllerField.value || ""}
                            row
                          >
                            {field.options.map((opt) =>
                              typeof opt === "string" ? (
                                <FormControlLabel
                                  key={opt}
                                  value={opt}
                                  control={<Radio />}
                                  label={opt}
                                />
                              ) : (
                                <FormControlLabel
                                  key={opt.value}
                                  value={opt.value}
                                  control={<Radio />}
                                  label={opt.label}
                                />
                              )
                            )}
                          </RadioGroup>
                          {errors?.[field.name] && (
                            <FormHelperText className={classes.helperText}>
                              {errors[field.name].message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                );

              default:
                return null;
            }
          };
          return (
            <React.Fragment key={field.name}>
              {renderConditionalField()}
              {field.conditionalFields &&
                Object.keys(field.conditionalFields).includes(
                  watchedValues?.[field.name]
                ) && (
                  <Grid item xs={12}>
                    <ConditionalFieldRenderer
                      fields={
                        field.conditionalFields[watchedValues[field.name]]
                      }
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                )}
            </React.Fragment>
          );
        })}
      </>
    </Grid>
  );
};

export default ConditionalFieldRenderer;
